import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useDebounce } from 'uikit';

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerIsUniqueContractSubObjectNameGql } from '../../workOrderQueries';

interface IUseUerIsUniqueContractSubObjectName {
    name: string;
    isDirtyObjectName?: boolean;
    contractObjectId?: number;
    contractSubObjectId?: number;
}

const requestId = 'uerIsUniqueContractSubObjectName';

export const useUerIsUniqueContractSubObjectName = (props: IUseUerIsUniqueContractSubObjectName) => {
    const dispatch = useDispatch();

    const { contractObjectId, contractSubObjectId, name, isDirtyObjectName } = props;

    const variables = useMemo(
        () => ({ contractObjectId, contractSubObjectId, name: name?.trim() }),
        [contractObjectId, contractSubObjectId, name],
    );

    const [
        uerIsUniqueContractSubObjectNameData,
        uerIsUniqueContractSubObjectNameStatus,
        uerIsUniqueContractSubObjectNameError,
        uerIsUniqueContractSubObjectNameRequest,
    ] = useRequest({
        requestId,
        query: uerIsUniqueContractSubObjectNameGql,
        withEffect: false,
    });

    const result = useMemo(
        () => uerIsUniqueContractSubObjectNameData?.uerIsUniqueContractSubObjectName?.result,
        [uerIsUniqueContractSubObjectNameData?.uerIsUniqueContractSubObjectName?.result],
    );

    const debounceRequest = useDebounce(() => {
        if (name && isDirtyObjectName) {
            dispatch(uerIsUniqueContractSubObjectNameRequest(variables));
        }
    }, 300);

    const [uerIsUniqueContractSubObjectNameReset] = useResetStoreData({ requestId });

    useEffect(() => {
        if (contractObjectId && name?.length) {
            debounceRequest();
        }
    }, [contractObjectId, debounceRequest, name?.length]);

    useEffect(
        () => () => {
            uerIsUniqueContractSubObjectNameReset(undefined);
        },
        [uerIsUniqueContractSubObjectNameReset],
    );

    return {
        result,
        uerIsUniqueContractSubObjectNameStatus,
        uerIsUniqueContractSubObjectNameError,
    };
};
